import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { CircularProgress, Typography } from '@material-ui/core'

const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const TILE_ATTR = '&copy; OpenStreetMap contributors'

const ROUTE_COLORS = {
  default: '#1f5e3a',
  mixed: '#2E7D32',
  combined: '#1565C0',
  split: '#E65100'
}

function makeIcon (color, label) {
  return L.divIcon({
    className: '',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    html: `<div style="
      width:28px;height:28px;border-radius:50%;
      background:${color};color:#fff;font-size:11px;font-weight:700;
      display:flex;align-items:center;justify-content:center;
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.3);
    ">${label}</div>`
  })
}

/**
 * DisposalRouteMap — shows OSRM driving route on a Leaflet map inside the expanded detail.
 *
 * Props:
 *   url          {string}  - API base URL
 *   hmiCoords    {{ lat, lng }}
 *   jobCoords    {{ lat, lng }}
 *   facilities   {Array<{ lat, lng, name }>} - 1+ facility stops
 *   routeColor   {string}  - optional color override
 *   height       {number}  - map height in px (default 260)
 */
const DisposalRouteMap = ({ url, hmiCoords, jobCoords, facilities, routeColor, height = 260 }) => {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!mapRef.current || !hmiCoords || !jobCoords || !facilities?.length) return

    // Clean up previous map
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove()
      mapInstanceRef.current = null
    }

    const map = L.map(mapRef.current, { zoomControl: true, attributionControl: true })
    mapInstanceRef.current = map
    L.tileLayer(TILE_URL, { attribution: TILE_ATTR, maxZoom: 18 }).addTo(map)

    const color = routeColor || ROUTE_COLORS.default

    // Build waypoints: HMI -> Jobsite -> Facility1 [-> Facility2...] -> HMI
    const waypoints = [
      hmiCoords,
      jobCoords,
      ...facilities.map(f => ({ lat: f.lat, lng: f.lng })),
      hmiCoords // return to yard
    ]

    // Add markers
    L.marker([hmiCoords.lat, hmiCoords.lng], { icon: makeIcon('#2E7D32', 'HMI') })
      .bindTooltip('HMI Yard', { permanent: false }).addTo(map)
    L.marker([jobCoords.lat, jobCoords.lng], { icon: makeIcon('#1565C0', 'JOB') })
      .bindTooltip('Jobsite', { permanent: false }).addTo(map)
    facilities.forEach((f, i) => {
      L.marker([f.lat, f.lng], { icon: makeIcon('#C62828', facilities.length > 1 ? `F${i + 1}` : 'F') })
        .bindTooltip(f.name || 'Facility', { permanent: false }).addTo(map)
    })

    // Fit bounds to all points
    const allPts = waypoints.map(w => [w.lat, w.lng])
    map.fitBounds(allPts, { padding: [30, 30] })

    // Fetch OSRM route geometry
    setLoading(true)
    setError('')
    axios.post(`${url}/api/aggregate-dashboard/fulfillment-calc/disposal/route-geometry`, { waypoints }, { timeout: 15000 })
      .then(res => {
        const geo = res.data?.geometry || []
        if (geo.length >= 2) {
          L.polyline(geo, { color, weight: 4, opacity: 0.8 }).addTo(map)

          // Add leg labels
          const legs = res.data?.legs || []
          const legLabels = ['HMI to Job', ...facilities.map((_f, i) => i === 0 ? 'Job to Facility' : `Facility ${i} to ${i + 1}`), 'Return to HMI']
          legs.forEach((leg, i) => {
            if (i < waypoints.length - 1) {
              const mid = {
                lat: (waypoints[i].lat + waypoints[i + 1].lat) / 2,
                lng: (waypoints[i].lng + waypoints[i + 1].lng) / 2
              }
              L.marker([mid.lat, mid.lng], {
                icon: L.divIcon({
                  className: '',
                  iconSize: [0, 0],
                  html: `<div style="
                    background:rgba(255,255,255,.92);padding:2px 6px;border-radius:4px;
                    font-size:9px;font-weight:600;color:#333;white-space:nowrap;
                    border:1px solid #ccc;box-shadow:0 1px 2px rgba(0,0,0,.15);
                    transform:translate(-50%,-50%);
                  ">${leg.distance} mi / ${leg.duration} min</div>`
                })
              }).bindTooltip(legLabels[i] || '', { permanent: false }).addTo(map)
            }
          })
        } else {
          // Fallback: straight lines
          L.polyline(allPts, { color, weight: 3, opacity: 0.5, dashArray: '8,6' }).addTo(map)
        }
      })
      .catch(err => {
        console.warn('[DisposalRouteMap] route geometry unavailable:', err.message)
        // Straight-line fallback
        L.polyline(allPts, { color, weight: 3, opacity: 0.5, dashArray: '8,6' }).addTo(map)
        setError('Route geometry unavailable — showing straight lines')
      })
      .finally(() => setLoading(false))

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [url, hmiCoords, jobCoords, facilities, routeColor])

  if (!hmiCoords || !jobCoords || !facilities?.length) {
    return (
      <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4, #97a094)', fontSize: 11 }}>
        Missing coordinates for route map
      </div>
    )
  }

  return (
    <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', border: '1px solid var(--line, #e0d8c2)' }}>
      <div ref={mapRef} style={{ width: '100%', height }} />
      {loading && (
        <div style={{
          position: 'absolute', top: 8, right: 8, background: 'rgba(255,255,255,.9)',
          borderRadius: 6, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 6,
          boxShadow: '0 1px 4px rgba(0,0,0,.15)', zIndex: 1000
        }}>
          <CircularProgress size={12} />
          <span style={{ fontSize: 10, color: '#333' }}>Loading route...</span>
        </div>
      )}
      {error && (
        <div style={{ position: 'absolute', bottom: 6, left: 6, zIndex: 1000 }}>
          <Typography variant="caption" style={{ color: '#a9341d', fontSize: 9, background: 'rgba(255,255,255,.85)', padding: '2px 6px', borderRadius: 4 }}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  )
}

export { ROUTE_COLORS }
export default DisposalRouteMap
